import React from "react";
import { X, Plus } from "lucide-react";
import { iconFor, CompetencyIconTile } from "./competencyIcons";

/** Grid card — one per included competency. Shows a single clamped rationale line
 * (already leads with that competency's own definition — see behavioral_role_
 * categories.py's _rationale). The "+" stepper is capped both by remaining time
 * budget (canGrow) and by the flat per-competency max (canGrow also folds in
 * count < maxPerCompetency) — two independent, simple bounds, not one clever one.
 * minQuestionsPerCompetency comes from the backend's /recommend response, not a
 * local import — see BehavioralAIStep2_Allocation's own comment below for why. */
const IncludedCard = ({ entry, canGrow, atMax, minQuestionsPerCompetency, onToggle, onCountChange }) => {
  const { competency_id, competency_name, count, rationale } = entry;

  return (
    <div className="rounded-2xl border border-[#5A4479]/20 bg-bgSecondary dark:glass-effect p-4 flex flex-col">
      <div className="flex items-start gap-2.5">
        <CompetencyIconTile name={competency_name} />
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold dark:text-white text-textPrimary truncate">
            {competency_name}
          </h4>
          {rationale && (
            <p className="text-xs dark:text-gray-400 text-textSecondary mt-0.5 leading-relaxed line-clamp-2" title={rationale}>
              {rationale}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onToggle(competency_id)}
          title="Remove this competency"
          className="shrink-0 p-1 rounded-md dark:text-gray-600 text-textSecondary hover:text-red-400 transition-colors"
        >
          <X size={13} />
        </button>
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#5A4479]/20">
        <span className="text-xs dark:text-gray-500 text-textSecondary">Questions</span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onCountChange(competency_id, Math.max(minQuestionsPerCompetency, count - 1))}
            disabled={count <= minQuestionsPerCompetency}
            className="w-6 h-6 rounded-full border border-[#5A4479]/50 dark:text-gray-400 text-textSecondary hover:border-[#5A4479] hover:text-[#DAC0FF] flex items-center justify-center text-xs transition-colors disabled:opacity-30 disabled:hover:border-[#5A4479]/50 disabled:hover:text-gray-400"
          >
            −
          </button>
          <span className="w-6 text-center text-sm dark:text-white text-textPrimary font-semibold">
            {count}
          </span>
          <button
            type="button"
            onClick={() => onCountChange(competency_id, count + 1)}
            disabled={!canGrow}
            title={atMax ? "Reached the per-competency max" : !canGrow ? "Time budget is fully allocated — remove a question elsewhere first" : undefined}
            className="w-6 h-6 rounded-full border border-[#5A4479]/50 dark:text-gray-400 text-textSecondary hover:border-[#5A4479] hover:text-[#DAC0FF] flex items-center justify-center text-xs transition-colors disabled:opacity-30 disabled:hover:border-[#5A4479]/50 disabled:hover:text-gray-400"
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
};

/** Tag-cloud pill — every not-yet-included competency. Disabled once the remaining
 * budget can't fit a new competency's minimum (3 questions), or once the competency
 * count itself is already at the max — two independent, simple caps, same idea as
 * the per-card stepper above. */
const MoreCompetencyPill = ({ entry, canAdd, disabledReason, onToggle }) => {
  const Icon = iconFor(entry.competency_name);
  return (
    <button
      type="button"
      onClick={() => onToggle(entry.competency_id)}
      disabled={!canAdd}
      title={!canAdd ? disabledReason : entry.description}
      className="group flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full border border-[#5A4479]/40 dark:text-gray-400 text-textSecondary hover:border-[#5A4479] hover:dark:text-[#DAC0FF] hover:text-[#5A4479] transition-colors text-xs font-medium disabled:opacity-30 disabled:hover:border-[#5A4479]/40 disabled:hover:text-gray-400"
    >
      <Icon size={12} className="shrink-0" />
      {entry.competency_name}
      <Plus size={11} className="shrink-0 opacity-60 group-hover:opacity-100" />
    </button>
  );
};

const BehavioralAIStep2_Allocation = ({
  allocation,
  targetQuestions,
  minQuestionsPerCompetency,
  maxQuestionsPerCompetency,
  minCompetencies,
  maxCompetencies,
  timeBudget,
  onToggle,
  onCountChange,
  onGenerate,
  onBack,
  isGenerating,
}) => {
  const included = allocation.filter((e) => e.included);
  const more = allocation.filter((e) => !e.included);
  const totalCount = included.reduce((sum, e) => sum + e.count, 0);
  const remaining = targetQuestions - totalCount;
  const canGenerate = included.length >= minCompetencies;
  const atCompetencyCap = included.length >= maxCompetencies;
  const usagePct = targetQuestions > 0 ? Math.min(100, Math.round((totalCount / targetQuestions) * 100)) : 0;

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <h1 className="text-lg font-light dark:text-white text-textPrimary">
          Review the allocation — remove any competency, or add more below.
        </h1>
        <div className="text-right shrink-0">
          <p className="text-sm dark:text-white text-textPrimary font-medium">
            {totalCount} of {targetQuestions} questions <span className="dark:text-gray-500 text-textSecondary font-normal">· {timeBudget} min</span>
          </p>
          <div className="mt-1.5 w-40 h-1 rounded-full bg-[#5A4479]/15 ml-auto">
            <div
              className="h-1 rounded-full bg-[#5A4479] transition-all"
              style={{ width: `${usagePct}%` }}
            />
          </div>
        </div>
      </div>

      {included.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {included.map((entry) => {
            const atMax = entry.count >= maxQuestionsPerCompetency;
            return (
              <IncludedCard
                key={entry.competency_id}
                entry={entry}
                canGrow={remaining > 0 && !atMax}
                atMax={atMax}
                minQuestionsPerCompetency={minQuestionsPerCompetency}
                onToggle={onToggle}
                onCountChange={onCountChange}
              />
            );
          })}
        </div>
      )}

      {!canGenerate && (
        <p className="text-sm text-amber-400 text-center">
          Include at least {minCompetencies} competencies to generate a set.
        </p>
      )}

      {more.length > 0 && (
        <div className="space-y-2.5">
          <h4 className="text-xs font-semibold uppercase tracking-wide dark:text-gray-500 text-textSecondary">
            More competencies
          </h4>
          <div className="flex flex-wrap gap-2">
            {more.map((entry) => {
              const canAdd = remaining >= minQuestionsPerCompetency && !atCompetencyCap;
              return (
                <MoreCompetencyPill
                  key={entry.competency_id}
                  entry={entry}
                  canAdd={canAdd}
                  disabledReason={
                    atCompetencyCap
                      ? `Already at the max of ${maxCompetencies} competencies`
                      : "Not enough time budget left for another competency"
                  }
                  onToggle={onToggle}
                />
              );
            })}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center w-full pt-2">
        <button
          onClick={onBack}
          className="border-btn dark:border-btn-dark px-6 py-3 rounded-full text-sm font-medium transition-all"
        >
          Back
        </button>
        <button
          onClick={onGenerate}
          disabled={isGenerating || !canGenerate}
          className="button px-6 py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {isGenerating ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>Generating...</span>
            </>
          ) : (
            <span>Generate Questions →</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default BehavioralAIStep2_Allocation;
